<template>
  <div class="page-container">
    <h2 class="page-title">订单管理</h2>

    <!-- 搜索栏 -->
    <div class="search-bar">
      <el-input v-model="searchKey" placeholder="请输入订单号或商品名称" style="width: 300px" clearable
        @keyup.enter="handleSearch" />
      <el-button type="primary" @click="handleSearch">查询</el-button>
      <el-button @click="resetSearch">重置</el-button>
    </div>

    <!-- 数据表格 -->
    <el-table :data="orderList" border v-loading="loading">
      <el-table-column prop="order_id" label="订单号" width="180" />
      <el-table-column prop="name" label="商品名称" width="200" />
      <el-table-column label="价格" width="100">
        <template #default="{ row }">￥{{ row.price }}</template>
      </el-table-column>
      <el-table-column prop="good_num" label="数量" width="80" />
      <el-table-column label="状态" width="120">
        <template #default="{ row }">
          <el-tag :type="statusTagMap[row.status]">
            {{ statusMap[row.status] }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column label="物流ID" width="150">
        <template #default="{ row }">
          <div class="logistics-id">
            <!-- 修改后的内容 -->
            <div class="id-container">
              <span v-if="row.wuliu_id" class="id-text">{{ row.wuliu_id }}</span>
              <el-button v-else type="text" @click.stop="openLogisticsDialog(row)">绑定物流</el-button>
              <el-icon class="edit-icon" @click.stop="openLogisticsDialog(row)">
                <Edit />
              </el-icon>
            </div>
          </div>
        </template>
      </el-table-column>

      <!-- 修改自提信息列 -->
      <el-table-column label="自提信息">
        <template #default="{ row }">
          <div class="pickup-info">
            <div v-if="row.ztstore_name">
              {{ row.ztstore_name }}<br />{{ row.ztstore_address }}
              <el-icon class="edit-icon" @click="openPickupDialog(row)">
                <Edit />
              </el-icon>
            </div>
            <span v-else class="text-gray">无自提信息</span>
          </div>
        </template>
      </el-table-column>

      <!-- 新增快递信息列 -->
      <el-table-column label="快递信息">
        <template #default="{ row }">
          <div v-if="row.kd_name" class="express-box">
            <div class="express-line">
              <span class="label">名称：</span>
              <span class="content">{{ row.kd_name }}</span>
              <el-icon class="edit-icon" @click="openExpressDialog(row)">
                <Edit />
              </el-icon>
            </div>
            <div class="express-line">
              <span class="label">电话：</span>
              <span class="content">{{ row.kd_phone }}</span>
            </div>
            <div class="express-line">
              <span class="label">地址：</span>
              <span class="content multiline">{{ row.kd_address }}</span>
            </div>
          </div>
          <span v-else class="text-gray">无快递信息</span>
        </template>
      </el-table-column>

      <el-table-column label="预约信息">
        <template #default="{ row }">
          <div v-if="row.reservat_informat">
            {{ row.reservat_informat }}

          </div>
          <span v-else class="text-gray">无预约信息</span>
        </template>
      </el-table-column>

      <el-table-column prop="create_time" label="创建时间" />
      <el-table-column label="操作" width="180" fixed="right">
        <template #default="{ row }">
          <el-button type="primary" size="small" @click="openEditDialog(row)">编辑</el-button>
          <el-popconfirm title="确定删除该订单？" @confirm="handleDelete(row.id)">
            <template #reference>
              <el-button type="danger" size="small">删除</el-button>
            </template>
          </el-popconfirm>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination">
      <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.limit"
        :total="pagination.total" :page-sizes="[10, 20, 50]" layout="total, sizes, prev, pager, next, jumper"
        @size-change="fetchData" @current-change="fetchData" />
    </div>

    <!-- 新增编辑弹窗 -->
    <el-dialog v-model="editDialog.visible" :title="editDialog.title" width="500px">
      <el-form :model="editForm">
        <!-- 自提信息表单 -->
        <template v-if="editDialog.type === 'pickup'">
          <el-form-item label="门店名称">
            <el-input v-model="editForm.ztstore_name" />
          </el-form-item>
          <el-form-item label="门店地址">
            <el-input v-model="editForm.ztstore_address" />
          </el-form-item>
        </template>

        <!-- 快递信息表单 -->
        <template v-if="editDialog.type === 'express'">
          <el-form-item label="收件人">
            <el-input v-model="editForm.kd_name" />
          </el-form-item>
          <el-form-item label="联系电话">
            <el-input v-model="editForm.kd_phone" />
          </el-form-item>
          <el-form-item label="收货地址">
            <el-input v-model="editForm.kd_address" />
          </el-form-item>
        </template>

        <!-- 物流ID表单 -->
        <template v-if="editDialog.type === 'logistics'">
          <el-form-item label="物流单号">
            <el-input v-model="editForm.wuliu_id" />
          </el-form-item>
        </template>
      </el-form>

      <template #footer>
        <el-button @click="editDialog.visible = false">取消</el-button>
        <el-button type="primary" @click="submitEditForm">保存</el-button>
      </template>
    </el-dialog>

    <!-- 编辑弹窗 -->
    <el-dialog v-model="dialogVisible" title="修改订单状态" width="500px">
      <el-form :model="formData" label-width="80px">
        <el-form-item label="订单状态">
          <el-select v-model="formData.status">
            <el-option label="待付款" :value="0" />
            <el-option label="待发货" :value="1" />
            <el-option label="待收货" :value="2" />
            <el-option label="已完成" :value="3" />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import Cookies from 'js-cookie'
import config from '@/config'
import axios from 'axios'

const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
const storeId = userInfo.id // 注意：这里获取的是用户ID，需根据业务调整

// 状态映射
const statusMap = {
  0: '待付款',
  1: '待发货',
  2: '待收货',
  3: '已完成'
}
const statusTagMap = {
  0: 'warning',
  1: 'info',
  2: 'primary',
  3: 'success'
}

// 数据相关
const loading = ref(false)
const searchKey = ref('')
const dialogVisible = ref(false)
const orderList = ref([])
const formData = reactive({
  id: null,
  status: 0
})

// 分页配置
const pagination = reactive({
  page: 1,
  limit: 10,
  total: 0
})

// 加载数据
const fetchData = async () => {
  try {
    loading.value = true
    const params = {
      store_id: storeId,
      page: pagination.page,
      limit: pagination.limit,
      search: searchKey.value
    }

    const { data } = await axios.get(`${config.BASE_URL}/shop/api/order_api.php`, { params })

    if (data.code === 200) {
      orderList.value = data.data.list.map(item => ({
        ...item,
        create_time: formatDateTime(item.create_time)
      }))
      pagination.total = data.data.pagination.total
    }
  } catch (error) {
    ElMessage.error('数据加载失败')
  } finally {
    loading.value = false
  }
}

// 搜索处理
const handleSearch = () => {
  pagination.page = 1
  fetchData()
}

// 重置搜索
const resetSearch = () => {
  searchKey.value = ''
  fetchData()
}

// 新增响应式数据
const editDialog = reactive({
  visible: false,
  type: '', // pickup/express/logistics
  title: '',
  currentId: null
})

const editForm = reactive({
  ztstore_name: '',
  ztstore_address: '',
  kd_name: '',
  kd_phone: '',
  kd_address: '',
  wuliu_id: ''
})

// 新增方法
const openPickupDialog = (row) => {
  editDialog.type = 'pickup'
  editDialog.title = '编辑自提信息'
  editDialog.currentId = row.id
  Object.assign(editForm, {
    ztstore_name: row.ztstore_name,
    ztstore_address: row.ztstore_address
  })
  editDialog.visible = true
}

const openExpressDialog = (row) => {
  editDialog.type = 'express'
  editDialog.title = '编辑快递信息'
  editDialog.currentId = row.id
  Object.assign(editForm, {
    kd_name: row.kd_name,
    kd_phone: row.kd_phone,
    kd_address: row.kd_address
  })
  editDialog.visible = true
}

const openLogisticsDialog = (row) => {
  editDialog.type = 'logistics'
  editDialog.title = row.wuliu_id ? '修改物流单号' : '绑定物流单号'
  editDialog.currentId = row.id
  editForm.wuliu_id = row.wuliu_id || ''
  editDialog.visible = true
}

// 提交编辑表单（所有类型）
const submitEditForm = async () => {
  try {
    const payload = {
      id: editDialog.currentId,
      store_id: storeId,
      ...editForm
    }

    // 请求配置
    const requestConfig = {
      params: {
        action: `update_${editDialog.type}` // 自动生成对应action
      }
    }

    const response = await axios.put(
      `${config.BASE_URL}/shop/api/order_api.php`,
      payload,
      requestConfig
    )

    if (response.data.code === 200) {
      ElMessage.success(response.data.message)
      editDialog.visible = false
      fetchData()
    } else {
      ElMessage.error(response.data.message || '操作失败')
    }
  } catch (error) {
    console.error('更新失败:', error)
    ElMessage.error(`操作失败: ${error.response?.data?.message || error.message}`)
  }
}

// 打开编辑
const openEditDialog = (row) => {
  formData.id = row.id
  formData.status = row.status
  dialogVisible.value = true
}

// 提交表单
const submitForm = async () => {
  try {
    await axios.put(`${config.BASE_URL}/shop/api/order_api.php`, {
      ...formData,
      store_id: storeId
    })
    ElMessage.success('更新成功')
    dialogVisible.value = false
    fetchData()
  } catch (error) {
    ElMessage.error('更新失败')
  }
}

// 删除处理
const handleDelete = async (id) => {
  try {
    await axios.delete(`${config.BASE_URL}/shop/api/order_api.php`, {
      data: { id, store_id: storeId }
    })
    ElMessage.success('删除成功')
    fetchData()
  } catch (error) {
    ElMessage.error('删除失败')
  }
}

// 日期格式化
const formatDateTime = (datetime) => {
  return datetime.replace('T', ' ').substr(0, 16)
}

// 初始化加载
onMounted(() => {
  fetchData()
})
</script>

<style scoped>
.page-title {
  margin-bottom: 20px;
  color: #333;
}

.search-bar {
  margin-bottom: 20px;
  display: flex;
  gap: 10px;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.text-gray {
  color: #999;
  font-style: italic;
}

.express-box {
  font-size: 13px;
  line-height: 1.6;
}

.express-line {
  display: flex;
  align-items: flex-start;
  margin-bottom: 2px;

  &:last-child {
    margin-bottom: 0;
  }
}

.label {
  color: #909399;
  flex: 0 0 45px;
  text-align: right;
  padding-right: 8px;
}

.content {
  flex: 1;
  word-break: break-all;

  &.multiline {
    white-space: pre-wrap;
  }
}

/* 保持原有样式 */
.text-gray {
  color: #999;
}

.edit-icon {
  margin-left: 8px;
  color: #409EFF;
  cursor: pointer;
  transition: color 0.3s;

  &:hover {
    color: #337ecc;
  }
}

.logistics-id {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>